import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { authAPI } from '../../auth/api/authApi';
// Importando Lucide Icons para um visual mais premium
import { Search, User, ShoppingBag, Menu as MenuIcon, LogOut } from 'lucide-react';
import logo from '../../../logo.jpg';
import styles from './Header.module.css';
import Menu from '../Menu/Menu';

const NAV_ITEMS = [{ label: 'MISSÃO', path: '/about' }];

function Header() {
    const [isMenuOpen, setIsOpen] = useState(false);
    const [user, setUser] = useState('Visitante');
    const [searchTerm, setSearchTerm] = useState('');
    const [cartCount, setCartCount] = useState(0);
    const [scrolled, setScrolled] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const isLoggedIn = !!localStorage.getItem('token');

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const updateCart = useCallback(() => {
        const savedCart = JSON.parse(localStorage.getItem('cart') || '[]');
        setCartCount(savedCart.length);
    }, []);

    useEffect(() => {
        updateCart();
        window.addEventListener('cartUpdated', updateCart);
        window.addEventListener('storage', updateCart);
        return () => {
            window.removeEventListener('cartUpdated', updateCart);
            window.removeEventListener('storage', updateCart);
        };
    }, [location, updateCart]);

    useEffect(() => {
        let isMounted = true;
        if (isLoggedIn) {
            authAPI.get_client_info()
                .then(data => { if (isMounted && data?.name) setUser(data.name); })
                .catch(() => isMounted && setUser('Visitante'));
        } else {
            setUser('Visitante');
        }
        return () => { isMounted = false; };
    }, [isLoggedIn]);

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchTerm.trim()) {
            navigate(`/products?search=${encodeURIComponent(searchTerm.trim())}`);
            setSearchTerm('');
        }
    };

    return (
        <header className={`${styles.header} ${scrolled ? styles.scrolled : ''}`}>
            <div className={styles.container}>

                {/* Esquerda: Menu e Nav */}
                <div className={styles.leftSection}>
                    <button className={styles.iconBtn} onClick={() => setIsOpen(true)}>
                        <MenuIcon size={22} strokeWidth={1.5} />
                    </button>
                    <nav className={styles.navDesktop}>
                        {NAV_ITEMS.map((item) => (
                            <Link key={item.path} to={item.path} className={location.pathname === item.path ? styles.active : ''}>
                                {item.label}
                                {location.pathname === item.path && (
                                    <motion.div layoutId="underline" className={styles.underline} />
                                )}
                            </Link>
                        ))}
                    </nav>
                </div>

                {/* Centro: Logo */}
                <motion.div
                    className={styles.logoWrapper}
                    onClick={() => navigate('/')}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    whileHover={{ scale: 1.02 }}
                >
                    <img src={logo} alt="Logo" className={styles.logoImg} />
                </motion.div>

                {/* Direita: Ações */}
                <div className={styles.rightSection}>
                    <form className={styles.searchBox} onSubmit={handleSearch}>
                        <Search size={18} className={styles.searchIcon} />
                        <input
                            type="text"
                            placeholder="Buscar..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </form>

                    <div className={styles.actionGroup}>
                        <button className={styles.iconBtn} onClick={() => navigate('/cart')}>
                            <ShoppingBag size={22} strokeWidth={1.5} />
                            <AnimatePresence>
                                {cartCount > 0 && (
                                    <motion.span
                                        initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}
                                        className={styles.cartBadge}
                                    >
                                        {cartCount}
                                    </motion.span>
                                )}
                            </AnimatePresence>
                        </button>

                        <div className={styles.userContainer}>
                            <button
                                className={styles.userPill}
                                onClick={() => navigate(isLoggedIn ? '/my-account' : '/login')}
                            >
                                <User size={18} />
                                <span className={styles.userName}>{user.split(' ')[0]}</span>
                            </button>


                        </div>
                        {isLoggedIn && (
                            <button className={styles.logoutBtn} onClick={() => { localStorage.removeItem('token'); navigate('/login'); }}>
                                <LogOut size={18} />
                            </button>
                        )}
                    </div>
                </div>
            </div>
            <Menu isOpen={isMenuOpen} onClose={() => setIsOpen(false)} />
        </header>
    );
}

export default Header;